/* ---------------------------------------------------------------------------
   "Log defect" — records a finding against a component of a structure.

   THE ONLY WAY A DEFECT IS LOGGED. It used to be one of two: the modeller's
   Defects inspector pinned a finding to a point on the 3D geometry, and migration
   0015 made both write to the same `defects` row. The modeller is not a
   destination any more — a model is only ever imported and an imported model is
   displayed rather than opened — so this dialog is it, and the component tree
   below is how a finding gets located.

   Which is why that tree is worth the code it costs. A defect whose component is
   "not recorded" is a defect nobody can find again on the structure.

   WHERE IS ASKED ONCE. It used to be asked twice — `Element`, the DoR inventory
   vocabulary every structure has, and `Component`, the modeller's own naming,
   offered only where a model existed. Every structure has a model now, so the
   second is both available and the more precise of the two, and asking for the
   same fact twice got two answers that could disagree. The component comes from
   the model's own tree, so it offers what is really there rather than a text box
   that invites a name the modeller has never heard of.

   AND IT IS ASKED AS A TREE. See componentPicker at the foot of this file for
   why the flat `<select>` it replaced could not be read.

   `element` is still a column and still shown on the rows that carry one — see
   RegisterDefect. It is the inspection form that rates condition per element, and
   that is where the vocabulary belongs.

   The component is OPTIONAL all the same. A defect somebody saw is worth
   recording even when they could not say precisely where, and forcing a location
   gets a wrong one; and the tree is a live report from the hero viewport, so it
   can be absent for a moment through no fault of the person typing.

   OPENED FROM THE 3D VIEW, it arrives already placed. "Log defect" on the
   record's 3D toolbar (src/modeler/meshView.ts) borrows a click on the model and
   opens this with the component that was hit and the exact point on the draft, so
   the finding is saved with a pin and drawn back onto the model. Opened from
   anywhere else it has no point, and the component tree is how it is located.

   Rendered into src/main.ts's dialog layer beside the others, on the same "no
   lifecycle of its own" basis.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS } from '../core/dom.ts';
import {
  actions,
  buildDefectQuantity,
  MAX_DEFECT_PHOTOS,
  type DefectDraft,
  type State,
} from '../core/store.ts';
import { approxKb, downscale } from '../core/downscale.ts';
import { button, field, scrim, seg } from '../components/widgets.ts';
import { flyout, menuButton, menuItem } from '../components/flyout.ts';
import { DEFECT_ICONS, SEVERITY_ICONS } from '../components/defectIcons.ts';
import { findBridge, inspectionKindLabel, prettyDate } from '../data/derive.ts';
import { DEFECT_CODES, DEFECT_SEVERITIES } from '../data/defects.ts';
import { componentTrail, pickerRows, searchComponents, type PickerRow } from '../data/componentTree.ts';
import { fieldsFor, MEASUREMENT_METHODS, methodLabel } from '../data/quantities.ts';
import { crackWidthRating } from '../data/ratingGuide.ts';
import { CLASS_DIAGNOSTIC, isCostable, treatmentFor, wordingMap } from '../data/remedial.ts';
import type { DefectQuantity } from '../data/types.ts';

/**
 * Whether moving from one draft to the next changes anything this dialog draws
 * beyond the control the change came from.
 *
 * Severity and category do — they light a button and name the finding in the
 * heading. So does the component, now that it is a tree rather than a `<select>`:
 * nothing echoes the choice back on its own, the breadcrumb and the pressed row
 * are both drawn from it, and the "Clear" button appears with it.
 *
 * So does the photograph: nothing but a re-render puts the thumbnail on screen in
 * place of the "Take a photograph" target, and it arrives from a file the person
 * chose rather than from a control holding it already.
 *
 * Date and note are not. They are drawn by nothing but their own controls, which
 * already hold what was typed, so typing into them costs no render at all — the
 * same rule as the inspection dialog.
 */
export function defectDialogNeedsPaint(a: DefectDraft, b: DefectDraft): boolean {
  return (
    a.severity !== b.severity ||
    a.code !== b.code ||
    a.component !== b.component ||
    a.photos !== b.photos ||
    a.pin !== b.pin ||
    a.method !== b.method ||
    implicationKey(a) !== implicationKey(b)
  );
}

/**
 * What the measurement panel is currently saying, as one string.
 *
 * The panel under the quantity boxes reads a typed width and reports which
 * Appendix D band and which repair method it falls in, which means it has to be
 * redrawn while somebody is typing — and this dialog's standing rule is that
 * typing into a control already holding what was typed costs no render at all.
 *
 * So the comparison is on the CONCLUSION, not the keystrokes. Typing the `6` of
 * `0.6` crosses a threshold and paints once; typing a second decimal that
 * changes nothing paints nothing. Same trick as `elementGates` in the inspection
 * dialog, for the same reason.
 */
function implicationKey(d: DefectDraft): string {
  const width = Number((d.quantity['widthMm'] ?? '').trim());
  const band = Number.isFinite(width) ? (crackWidthRating(width)?.rating ?? -1) : -1;

  /* The treatment is chosen by the same measurements, so it moves on the same
     keystrokes — and it has its own thresholds, which are NOT Appendix D's. */
  let quantity: DefectQuantity | null = null;
  try {
    quantity = buildDefectQuantity(d);
  } catch {
    /* A half-typed measurement is not an error worth reporting mid-keystroke.
       The save path reports it; here it just means no conclusion yet. */
  }
  const treatment = treatmentFor(d.code, quantity)?.id ?? '-';

  return `${d.code}|${band}|${treatment}`;
}

/** The dialog, or null when it is closed. */
export function defectDialog(s: Readonly<State>): HTMLElement | null {
  if (!s.defectBridgeId) return null;
  const b = findBridge(s.defectBridgeId);
  /* No record to hang this on, which happens only where the register is
     empty. Closed rather than drawn over nothing — the same answer this
     dialog already gives when the thing it was opened for has gone. */
  if (b === null) return null;
  const d = s.defectDraft;

  const setField = (key: keyof DefectDraft) => (e: Event) =>
    actions.setDefectField(
      key,
      (e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value,
    );

  /* Straight out of the model this structure has. The record already holds the
     tree for the selected structure — see heroComponentTree — so this is a read,
     not a request, and it is the same tree the Defects card hangs its rows off. */
  const hasTree = (s.heroComponentTree?.length ?? 0) > 0;

  return h(
    'div',
    {
      class: 'dlg',
      ...scrim(() => actions.closeLogDefect(), () => s.defectSaving),
    },
    h(
      'form',
      {
        class: 'dlg__panel dlg__panel--sm def-dlg',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': `Log inspection — ${b.name}`,
        onsubmit: (e: Event) => {
          e.preventDefault();
          void actions.saveDefect();
        },
      },

      h(
        'div',
        { class: 'dlg__head' },
        h(
          'div',
          {},
          h('div', { class: 'dlg__kicker', text: `${b.id} · inspection` }),
          h('h2', { class: 'dlg__title', text: 'Log inspection' }),
        ),
        h('span', { class: 'spacer' }),
        h(
          'button',
          {
            type: 'button',
            class: 'dlg__close',
            title: 'Cancel',
            'aria-label': 'Cancel',
            onclick: () => actions.closeLogDefect(),
          },
          '×',
        ),
      ),

      s.defectError
        ? h('div', { class: 'dlg__note dlg__note--error' }, h('span', { text: s.defectError }))
        : null,

      h(
        'div',
        { class: 'dlg__body' },
        h(
          'div',
          { class: 'dlg__form-grid' },

          /* What, when and how bad, on one line: three short answers that are
             read together, and stacking them cost the tree below its room. */
          h(
            'div',
            { class: 'def-top', style: 'grid-column: 1 / -1' },
            field('Category', categoryPicker(s, d), 'def-code'),

            field(
              'Seen on',
              h('input', {
                id: 'def-date',
                class: 'input',
                type: 'date',
                required: true,
                value: d.loggedAt,
                oninput: setField('loggedAt'),
              }),
              'def-date',
            ),

            /* Which visit found it (LC-62), so a later one can be told apart. */
            field(
              'Found on',
              h(
                'select',
                { id: 'def-insp', class: 'select', onchange: setField('inspectionId') },
                h('option', { value: '', text: 'No inspection named' }),
                ...b.inspections
                  .filter((i) => i.id)
                  .slice()
                  .sort((x, y) => y.date.localeCompare(x.date))
                  .map((i) => h('option', { value: i.id!, selected: i.id === d.inspectionId, text: `${prettyDate(i.date)} · ${inspectionKindLabel(i)}` })),
              ),
              'def-insp',
            ),

            /* Its own row: under Category alone the column is too narrow for
               all four, and Critical was cut off. */
            h(
              'div',
              { style: 'grid-column: 1 / -1' },
              field(
                'Severity',
                seg(
                  DEFECT_SEVERITIES.map((opt) => ({
                    label: opt,
                    leadIcon: SEVERITY_ICONS[opt],
                    on: d.severity === opt,
                    onClick: () => actions.setDefectField('severity', opt),
                  })),
                ),
              ),
            ),
          ),

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field(
              'Component',
              hasTree
                ? componentPicker(s, d)
                : /* The viewport has not reported a tree yet — it is still loading,
                     or this structure's model has never been rendered. Disabled
                     rather than swapped for a text box: a typed name that matches
                     nothing in the model would place the defect nowhere on the
                     card, and the defect can be recorded without one. */
                  h('input', {
                    id: 'def-component',
                    class: 'input',
                    disabled: true,
                    placeholder: 'Waiting for the 3D view to report its components',
                    value: '',
                  }),
              'def-component',
            ),
          ),

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field(
              'Note',
              h('textarea', {
                id: 'def-note',
                class: 'input',
                rows: 3,
                placeholder: 'What was seen, and where on the component',
                value: d.note,
                oninput: setField('note'),
              }),
              'def-note',
            ),
          ),

          /* The evidence straight under the claim it supports, and above the
             measurement — which is optional, and which is often taken off one
             of these photographs. */
          h('div', { style: 'grid-column: 1 / -1' }, photoField(d)),

          h('div', { style: 'grid-column: 1 / -1' }, measurementField(d, s)),
        ),
      ),

      h(
        'div',
        { class: 'dlg__foot def-foot' },
        h('span', { class: 'spacer' }),
        button('Cancel', { onClick: () => actions.closeLogDefect() }),
        button(s.defectSaving ? 'Recording…' : 'Record inspection', {
          primary: true,
          type: 'submit',
          disabled: s.defectSaving,
        }),
      ),
    ),
  );
}

/**
 * The category, as a button that opens a list — each one with its icon.
 *
 * NOT A `<select>` any more, because an `<option>` cannot hold an icon and the
 * icon is how the category is found in a list of nine codes. The list is the
 * application's own flyout (src/components/flyout.ts): drawn from the store, so a
 * repaint redraws it as it was, and closed by choosing, by Escape or by pressing
 * outside it — the same menu the title bar and the ribbon use.
 */
function categoryPicker(s: Readonly<State>, d: DefectDraft): HTMLElement {
  const chosen = DEFECT_CODES.find((c) => c.code === d.code);
  return h(
    'div',
    { class: 'csel' },
    h(
      'button',
      {
        id: 'def-code',
        type: 'button',
        class: 'select csel__btn',
        ...menuButton(s, 'defect-code'),
        'aria-haspopup': 'listbox',
      },
      icon(DEFECT_ICONS[d.code] ?? ICONS.alert, { size: 16, width: 1.6, class: 'csel__icon' }),
      h('span', { class: 'csel__code', text: d.code }),
      h('span', { class: 'csel__label ellipsis', text: chosen?.label ?? d.code }),
      icon(ICONS.chevronDown, { size: 12, width: 2, class: 'csel__chev' }),
    ),
    flyout(
      s,
      'defect-code',
      { label: 'Issue category', width: 260 },
      h(
        'div',
        { class: 'flyout__list' },
        ...DEFECT_CODES.map((c) =>
          menuItem({
            label: c.label,
            aside: c.code,
            iconPath: DEFECT_ICONS[c.code] ?? ICONS.alert,
            checked: c.code === d.code,
            onClick: () => actions.setDefectField('code', c.code),
          }),
        ),
      ),
    ),
  );
}

/* ---------------------------------------------------------------------------
   How big it is.

   `QC-02`. The Bridge Inspection Manual asks for a quantity twice — §4.3 and
   §5.4 — and both times it asks so that a COST can be prepared. Until this
   existed the answer went into the note, where nothing could compute on it and
   nothing could tell a measured figure from a guess.

   WHICH BOXES APPEAR DEPENDS ON THE CATEGORY. A crack has a length and a width;
   a scour hole has a depth below bed level in metres. Offering every dimension
   to every category would invite a scour depth typed into a millimetre box, and
   the register would carry a number three orders of magnitude wrong in a column
   the cost engine reads.

   NOTHING HERE IS REQUIRED. An inspector without a tape still records the
   finding — that is the whole point of a defect register, and a form that
   refused an unmeasured one would lose the finding to save the measurement. What
   IS required is that a number, if there is one, says where it came from.

   THE METHOD IS NOT PRE-SELECTED. A default of "measured" would be the
   application making a claim on the inspector's behalf, and it is precisely the
   claim this item exists to stop it making. So the segmented control starts with
   nothing chosen and the save path refuses a measurement without one.
   --------------------------------------------------------------------------- */

function measurementField(d: DefectDraft, s: Readonly<State>): HTMLElement {
  const fields = fieldsFor(d.code);
  const chosen = d.method.trim();

  return h(
    'div',
    { class: 'dlg__block' },
    field(
      'Measurement — optional',
      h(
        'div',
        {},
        fields.length
          ? h(
              'div',
              { class: 'qgrid' },
              ...fields.map((f) =>
                h(
                  'label',
                  { class: 'qgrid__row', ...(f.hint ? { title: f.hint } : {}) },
                  h('span', { class: 'qgrid__label ellipsis', text: f.label }),
                  h(
                    'span',
                    { class: 'qgrid__box' },
                    h('input', {
                      class: 'input qgrid__input',
                      type: 'number',
                      step: f.integer ? '1' : 'any',
                      min: '0',
                      max: String(f.max),
                      placeholder: '—',
                      'aria-label': `${f.label} in ${f.unit}`,
                      value: d.quantity[f.key] ?? '',
                      oninput: (e: Event) =>
                        actions.setDefectQuantity(f.key, (e.target as HTMLInputElement).value),
                    }),
                    /* The unit is on screen in the box, always — drawn over it,
                       not as placeholder text, so it is still there once a
                       number is typed. It is also in the column name and in what
                       is stored; this is the one place a person reads it, and
                       the one place where getting it wrong costs an order of
                       magnitude. */
                    h('span', { class: 'qgrid__unit', 'aria-hidden': 'true', text: f.unit }),
                  ),
                ),
              ),
            )
          : h('div', {
              class: 'dlg__hint',
              text: 'This category has no measurements defined.',
            }),

        h(
          'div',
          { class: 'qmethod' },
          h('span', { class: 'qmethod__cap', text: 'How was it obtained?' }),
          seg(
            MEASUREMENT_METHODS.map((opt) => ({
              label: methodLabel(opt),
              on: chosen === opt,
              onClick: () => actions.setDefectField('method', chosen === opt ? '' : opt),
            })),
          ),
        ),

        /* Only where the answer makes it mean something. An instrument recorded
           against an estimate would be a claim that an instrument was used. */
        chosen === 'measured'
          ? h(
              'label',
              { class: 'qinstrument' },
              h('span', { class: 'qinstrument__cap', text: 'Instrument' }),
              h('input', {
                class: 'input',
                type: 'text',
                maxlength: '60',
                placeholder: 'Crack width gauge, tape, rod, level',
                'aria-label': 'What the measurement was taken with',
                value: d.instrument,
                oninput: (e: Event) =>
                  actions.setDefectField('instrument', (e.target as HTMLInputElement).value),
              }),
            )
          : null,

        implications(d, s),
      ),
    ),
  );
}

/**
 * What the measurement means, while it is being typed.
 *
 * TWO SETS OF THRESHOLDS, NAMED SEPARATELY, and that separation is the point.
 * Appendix D rates a crack Good below 0.3 mm and Fair to 1 mm; ACI 546R and
 * EN 1504-9 switch repair method at 0.2 mm and 1 mm. They answer different
 * questions — one assigns a condition, the other selects a material — and an
 * inspector looking at a 0.25 mm crack is entitled to see that it is in good
 * condition AND wants a surface seal rather than injection. Averaging the two
 * into one set of bands would produce a scale that is neither.
 *
 * THE APPENDIX D LINE NAMES ITS OWN SCOPE. The numeric bands are printed on the
 * deck row of the rating guide, and this dialog does not ask which element the
 * defect is on — it asks for a component from the model's own tree, deliberately,
 * because asking "where" twice got two answers that could disagree. So rather
 * than guess an element, the panel quotes the rule and says which elements it is
 * written for, and leaves applying it to the person holding the gauge. Where an
 * element IS known — the printed summary sheet, the record — `suggestConditionFor`
 * does the gating properly.
 */
function implications(d: DefectDraft, s: Readonly<State>): HTMLElement | null {
  const rows: HTMLElement[] = [];

  const typedWidth = (d.quantity['widthMm'] ?? '').trim();
  if (d.code === 'CRK' && typedWidth !== '') {
    const width = Number(typedWidth);
    const band = Number.isFinite(width) ? crackWidthRating(width) : null;
    if (band) {
      rows.push(
        h(
          'div',
          { class: 'qimp__row' },
          h('span', { class: 'qimp__k', text: 'Appendix D' }),
          h(
            'span',
            { class: 'qimp__v' },
            h('strong', { text: `${band.label} (${band.rating})` }),
            h('span', { text: ` — “${band.rule}”, on concrete deck main members and cross girders. ` }),
            h('span', {
              class: 'qimp__caveat',
              text: 'Another element is rated by its own row of the guide, which is not numeric.',
            }),
          ),
        ),
      );
    }
  }

  let quantity: DefectQuantity | null = null;
  try {
    quantity = buildDefectQuantity(d);
  } catch {
    /* Mid-keystroke. The save path is where an invalid measurement is reported. */
  }

  const treatment = treatmentFor(d.code, quantity, wordingMap(s.treatmentWording));
  if (treatment) {
    rows.push(
      h(
        'div',
        { class: 'qimp__row' },
        h('span', { class: 'qimp__k', text: 'Remedial' }),
        h(
          'span',
          { class: 'qimp__v' },
          h('strong', { text: treatment.label }),
          h('span', {
            class: 'qimp__class',
            title: CLASS_DIAGNOSTIC[treatment.klass],
            text: treatment.klass,
          }),
          h('span', { text: ` — ${treatment.note}` }),
          isCostable(treatment.klass)
            ? null
            : h('span', {
                class: 'qimp__caveat',
                text: ' This class is not costed by the register: DoR’s own programme document says the estimate comes after an investigation and a design.',
              }),
        ),
      ),
    );
  }

  return rows.length ? h('div', { class: 'qimp' }, ...rows) : null;
}

/* ---------------------------------------------------------------------------
   The photographs.

   THE EVIDENCE FOR THE NOTE. "Hairline crack at the soffit" is a claim; the
   photograph is what lets an engineer at a desk in Kathmandu agree or disagree
   with it without going to the river. The register has been able to STORE one
   against a defect since migration 0015 and to serve it since defect-photo.ts —
   the record draws the tile, the lightbox enlarges it — but the only writer was
   the modeller's own reconcile, and the modeller stopped being a destination. So
   the tile existed for photographs nothing could take.

   SEVERAL, up to MAX_DEFECT_PHOTOS. A defect is one finding in one place, but
   one place is not one view of it: the close-up with a gauge in frame says how
   bad, the wider shot says which girder. Migration 0033 is where the rest go.

   THE CAMERA, not the file picker, wherever the browser honours `capture` — the
   photograph an inspector wants is the one they have not taken yet. It is a HINT
   and a desk browser falls back to choosing files, several at once, which is the
   right behaviour for somebody entering a backlog of paper forms.

   DOWNSCALED ON PICK rather than on submit, the same way and through the same
   code as an inspection's: it is where a file the browser cannot decode can be
   reported against the file the person just chose, and it means the size under
   each thumbnail is the size that will actually be sent. That matters more here
   than on the inspection form, because a defect with no signal behind it goes
   into an IndexedDB queue on a phone rather than onto the wire.
   --------------------------------------------------------------------------- */

function photoField(d: DefectDraft): HTMLElement {
  const full = d.photos.length >= MAX_DEFECT_PHOTOS;

  const input = h('input', {
    type: 'file',
    accept: 'image/*',
    capture: 'environment',
    multiple: true,
    class: 'visually-hidden',
    onchange: (e: Event) => {
      const target = e.target as HTMLInputElement;
      const files = Array.from(target.files ?? []);
      // Cleared so picking the same file twice in a row still fires a change.
      target.value = '';
      void (async () => {
        for (const file of files) {
          try {
            const shrunk = await downscale(file);
            actions.addDefectPhoto(shrunk.dataUrl);
          } catch (err) {
            actions.setDefectError(err instanceof Error ? err.message : 'That file could not be read.');
          }
        }
      })();
    },
  });

  return field(
    d.photos.length ? `Photographs (${d.photos.length} of ${MAX_DEFECT_PHOTOS})` : 'Photographs',
    /* The inspection form's own photo strip, classes and all. Two dialogs
       asking for the same thing should not look like two different features,
       and a second set of near-identical rules is a second thing to keep in
       step with the first. */
    h(
      'div',
      { class: 'insp-photos' },
      ...d.photos.map((dataUrl, i) =>
        h(
          'span',
          { class: 'insp-photos__item' },
          h('img', { class: 'insp-photos__img', src: dataUrl, alt: `Photograph ${i + 1} of this issue` }),
          h('span', { class: 'insp-photos__size', text: `${approxKb(dataUrl)} kB` }),
          h('button', {
            type: 'button',
            class: 'imgslot__clear',
            title: 'Remove this photograph',
            'aria-label': `Remove photograph ${i + 1}`,
            text: '✕',
            onclick: () => actions.removeDefectPhoto(i),
          }),
          /* `QC-08`, on each photograph rather than once for the defect: the one
             with the gauge in frame is the one that can be measured on. The
             natural dimensions come off the decoded thumbnail, because they are
             the frame every distance is computed in and only the DOM knows them. */
          h('button', {
            type: 'button',
            class: 'insp-photos__measure',
            title: 'Measure the issue on this photograph',
            'aria-label': `Measure the issue on photograph ${i + 1}`,
            text: 'Measure',
            onclick: (e: Event) => {
              const img = (e.currentTarget as HTMLElement)
                .closest('.insp-photos__item')
                ?.querySelector<HTMLImageElement>('img');
              /* naturalWidth is 0 until the image has decoded. Opening on a
                 zero would divide by zero in the scale arithmetic; refusing
                 to open and leaving the button is better than a dialog that
                 silently cannot produce a number. */
              if (img?.naturalWidth) actions.openMeasure(i, img.naturalWidth, img.naturalHeight);
            },
          }),
        ),
      ),
      full
        ? null
        : h(
            'button',
            {
              type: 'button',
              class: 'insp-photos__add',
              title: 'Photograph the issue',
              onclick: (e: Event) =>
                (e.currentTarget as HTMLElement).querySelector<HTMLInputElement>('input')?.click(),
            },
            icon(ICONS.camera, { size: 16, stroke: 'var(--txt3)' }),
            h('span', { text: 'Add' }),
            input,
          ),
    ),
  );
}

/* ---------------------------------------------------------------------------
   The component picker.

   This was a `<select>` over every component in the model, flattened to its
   qualified name — a hundred-odd options reading "Abutment-1 Bearing Lines
   Deck-1 B-3", alphabetically, with no indication of which of those four words
   is the thing you are looking for. On a real viaduct it scrolled past a
   screenful and the structure that made the names meaningful was thrown away to
   produce them.

   So it is the tree it came from. Roots open, everything below folded: two
   levels on screen, and a third when you open one. What is STORED is unchanged —
   the qualified name, which is what a 3D pick writes and what the Defects card
   places on — and the breadcrumb above the tree shows exactly that, split back
   into its parts so it can be read.

   NOT A COMBOBOX. A tree with the full ARIA keyboard contract (arrow keys, type
   -ahead, one tab stop for the whole widget) is a real widget and this is not
   pretending to be one: every row is an ordinary button, so Tab and Enter work
   the way they do everywhere else in the form. Folded-by-default is what keeps
   that from being hundreds of tab stops — only rows on screen exist at all.
   --------------------------------------------------------------------------- */

function componentPicker(s: Readonly<State>, d: DefectDraft): HTMLElement {
  const roots = s.heroComponentTree ?? [];

  /* CHOSEN: the choice is all that is shown — WHAT IS RECORDED, in as many
     words, split back into the route to it. The tree is for finding a component,
     and "Change" brings it back to find another. */
  if (d.component) {
    const trail = componentTrail(roots, d.component);
    return h(
      'div',
      { class: 'ctree' },
      h(
        'div',
        { class: 'ctree__chosen' },
        icon(ICONS.cube, { size: 15, width: 1.5, class: 'ctree__icon' }),
        h(
          'span',
          { class: 'ctree__trail' },
          ...trail.flatMap((part, i) => [
            i > 0 ? h('span', { class: 'ctree__sep', text: '›' }) : null,
            h('span', {
              // The last part is the component itself; the ones before it are
              // the route to it and read as context, not as the answer.
              class: i === trail.length - 1 ? 'ctree__leaf' : 'ctree__crumb',
              text: part,
            }),
          ]),
        ),
        h('span', { class: 'spacer' }),
        h(
          'button',
          {
            type: 'button',
            class: 'ctree__clear',
            title: 'Choose a different component, or none',
            onclick: () => actions.setDefectField('component', ''),
          },
          'Change',
        ),
      ),
    );
  }

  /* FINDING. Typed into, the box swaps the tree for the components whose names
     contain what was typed — on a viaduct the answer to "where is girder G4 of
     span 7" is several levels down whichever branch it is in. */
  const query = s.defectTreeFilter;
  const rows = query.trim() ? searchComponents(roots, query) : pickerRows(roots, new Set(s.defectTreeOpen));

  return h(
    'div',
    { class: 'ctree' },
    h(
      'label',
      { class: 'ctree__find' },
      icon(ICONS.search, { size: 14, width: 1.6, class: 'ctree__icon' }),
      h('input', {
        // The id is what keeps focus across the repaint each keystroke causes.
        id: 'def-component-find',
        class: 'ctree__find-input',
        type: 'search',
        placeholder: 'Find a component — pier, girder, bearing…',
        'aria-label': 'Find a component',
        autocomplete: 'off',
        value: query,
        oninput: (e: Event) => actions.setDefectTreeFilter((e.target as HTMLInputElement).value),
      }),
    ),
    rows.length
      ? h('div', { class: 'ctree__scroll' }, ...rows.map((row) => pickerRow(row, d.component, d.componentId)))
      : h('div', {
          class: 'ctree__empty',
          text: query.trim()
            ? `No component's name contains “${query.trim()}”.`
            : /* A model is loaded and its tree is empty, which is not the same as
                 no tree at all — that case never reaches here, because the
                 dialog draws the disabled field instead. */
              'This model reported no components.',
        }),
  );
}

/** One row: a twisty where there is something under it, and the label. */
function pickerRow(row: PickerRow, chosen: string, chosenId: string): HTMLElement {
  /* By id where both have one (LC-14): a Pile-1 picked under one pile group is
     not lit under all four. */
  const on = row.isComponent && (chosenId && row.id ? row.id === chosenId : row.value === chosen);
  const toggle = (): void => actions.toggleDefectTreeNode(row.path);

  const twist = row.hasChildren
    ? h(
        'button',
        {
          type: 'button',
          class: 'ctree__twist',
          'aria-expanded': row.isOpen ? 'true' : 'false',
          'aria-label': `${row.isOpen ? 'Collapse' : 'Expand'} ${row.label}`,
          onclick: toggle,
        },
        icon(ICONS.chevronDown, {
          size: 10,
          width: 2,
          class: row.isOpen ? 'grp__chev' : 'grp__chev grp__chev--closed',
        }),
      )
    : h('span', { class: 'ctree__twist ctree__twist--leaf' });

  return h(
    'div',
    {
      class: ['ctree__row', on && 'is-on', !row.isComponent && 'ctree__row--group'],
      style: { paddingLeft: `${6 + row.depth * 15}px` },
    },
    twist,
    row.isComponent
      ? /* Selectable. The twisty is a separate control beside it, so opening a
           component to see its parts does not also log the defect against it —
           "Bearing Lines" and "Bearing Lines Deck-1 B-3" are different answers
           and both are ones somebody might mean. */
        h(
          'button',
          {
            type: 'button',
            class: 'ctree__pick',
            'aria-pressed': on ? 'true' : 'false',
            title: row.value,
            onclick: () => actions.setDefectComponent(row.value, row.id),
          },
          h('span', { class: 'ctree__label', text: row.label }),
        )
      : /* A work-breakdown group. It nests components and cannot carry a defect,
           so the whole row is the twisty rather than a control that looks
           choosable and refuses. */
        h(
          'button',
          { type: 'button', class: 'ctree__pick ctree__pick--group', onclick: toggle },
          h('span', { class: 'ctree__label', text: row.label }),
        ),
  );
}
